import React, { useEffect, useState } from "react";
import {
    Settings,
    FlaskConical,
    Rocket,
    Smartphone,
    Loader,
    CheckCircle,
    AlertCircle,
    ChevronRight,
    Calculator,
    MapPin,
} from "lucide-react";
import PageLayout from "../components/PageLayout";
import { useAuth } from "../context/AuthContext";
import { getAppSettings, updateAppSettings, AppSettings } from "../api/app-settings";

interface SettingsPageProps {
    onLogout?: () => void;
    onNavigate?: (page: string) => void;
}

// ─── Reusable primitives ──────────────────────────────────────────────────────

interface SettingsSectionProps {
    title: string;
    description: string;
    icon: React.ElementType;
    children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, description, icon: Icon, children }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg">
                <Icon className="w-4 h-4 text-blue-600" />
            </div>
            <div>
                <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            </div>
        </div>
        <div className="divide-y divide-gray-100">{children}</div>
    </div>
);

interface ToggleRowProps {
    label: string;
    description: string;
    checked: boolean;
    onChange: (value: boolean) => void;
    saving: boolean;
    badge?: React.ReactNode;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ label, description, checked, onChange, saving, badge }) => (
    <div className="px-6 py-5 flex items-center justify-between gap-6">
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900">{label}</span>
                {badge}
            </div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>
        </div>
        <button
            onClick={() => !saving && onChange(!checked)}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 ${
                checked ? "bg-blue-600" : "bg-gray-200"
            }`}
            aria-checked={checked}
            role="switch"
        >
            <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    checked ? "translate-x-6" : "translate-x-1"
                }`}
            />
        </button>
    </div>
);

// ─── Mode badge ───────────────────────────────────────────────────────────────

const ModeBadge: React.FC<{ devMode: boolean }> = ({ devMode }) =>
    devMode ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
            <FlaskConical className="w-3 h-3" />
            DEV MODE
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
            <Rocket className="w-3 h-3" />
            PRODUCTION
        </span>
    );

// ─── Toast ────────────────────────────────────────────────────────────────────

type ToastState = { type: "success" | "error"; message: string } | null;

const Toast: React.FC<{ toast: ToastState }> = ({ toast }) => {
    if (!toast) return null;
    const isSuccess = toast.type === "success";
    return (
        <div
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all ${
                isSuccess
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
            }`}
        >
            {isSuccess ? (
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
            ) : (
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
            )}
            {toast.message}
        </div>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const SettingsPage: React.FC<SettingsPageProps> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();
    const [settings, setSettings] = useState<AppSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastState>(null);

    const showToast = (type: "success" | "error", message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        if (!user?.tenant_id) return;
        setLoading(true);
        getAppSettings(user.tenant_id)
            .then(setSettings)
            .catch(() => showToast("error", "Failed to load settings."))
            .finally(() => setLoading(false));
    }, [user?.tenant_id]);

    const handleToggle = async (key: keyof Pick<AppSettings, "dev_mode" | "da_requires_visit" | "ta_requires_visit" | "location_update_enabled">, value: boolean) => {
        if (!user?.tenant_id) return;

        // Optimistic update — flip immediately so the toggle feels instant
        setSettings(prev => prev ? { ...prev, [key]: value } : null);
        setSaving(true);

        try {
            const updated = await updateAppSettings(user.tenant_id, { [key]: value });
            setSettings(updated);
            const messages: Record<string, [string, string]> = {
                dev_mode:                 ["Developer mode ON — single-device login disabled.", "Production mode ON — single-device login enforced."],
                da_requires_visit:        ["DA will only be calculated when a visit is recorded.", "DA will always be calculated on sign-out."],
                ta_requires_visit:        ["TA will only be calculated when a visit is recorded.", "TA will always be calculated on sign-out."],
                location_update_enabled:  ["Location updates enabled — MRs can update doctor/firm coordinates from mobile.", "Location updates disabled — MRs cannot update coordinates from mobile."],
            };
            const [onMsg, offMsg] = messages[key] ?? ["Setting saved.", "Setting saved."];
            showToast("success", value ? onMsg : offMsg);
        } catch {
            // Revert on failure
            setSettings(prev => prev ? { ...prev, [key]: !value } : null);
            showToast("error", "Failed to save setting. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageLayout onLogout={onLogout} currentPage="settings" onNavigate={onNavigate}>
            <div className="p-6 max-w-3xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                        <span>Admin</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                        <span className="text-gray-700 font-medium">Settings</span>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">App Settings</h1>
                    <p className="text-gray-500 mt-1 text-sm">
                        Manage application-wide configuration for your tenant.
                    </p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader className="w-7 h-7 text-blue-600 animate-spin" />
                        <span className="ml-3 text-gray-500 text-sm">Loading settings…</span>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* ── Device Login ──────────────────────────────── */}
                        <SettingsSection
                            title="Device Login"
                            description="Controls single-device login enforcement across the app."
                            icon={Smartphone}
                        >
                            <ToggleRow
                                label="Developer Mode — Disable Single-Device Login"
                                description="When ON, users can log in from multiple devices simultaneously. Turn this OFF in production to restrict each user to one device at a time."
                                checked={settings?.dev_mode ?? false}
                                onChange={(val) => handleToggle("dev_mode", val)}
                                saving={saving}
                                badge={<ModeBadge devMode={settings?.dev_mode ?? false} />}
                            />
                        </SettingsSection>

                        {settings?.dev_mode && (
                            <div className="flex items-start gap-3 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                                <Smartphone className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                <span>
                                    <strong>Single-device login is currently disabled.</strong> Users can log in from any device. Switch to Production mode to enforce single-device login.
                                </span>
                            </div>
                        )}

                        {/* ── Allowance Calculation ─────────────────── */}
                        <SettingsSection
                            title="Allowance Calculation"
                            description="Controls when DA and TA are calculated during attendance sign-out."
                            icon={Calculator}
                        >
                            <ToggleRow
                                label="DA Requires Visit"
                                description="When ON, Daily Allowance is only calculated if the employee has recorded at least one Doctor Visit or Firm Visit on the same day. When OFF, DA is always calculated on sign-out."
                                checked={settings?.da_requires_visit ?? false}
                                onChange={(val) => handleToggle("da_requires_visit", val)}
                                saving={saving}
                            />
                            <ToggleRow
                                label="TA Requires Visit"
                                description="When ON, Travel Allowance is only calculated if the employee has recorded at least one Doctor Visit or Firm Visit on the same day. When OFF, TA is always calculated on sign-out (provided a route is assigned)."
                                checked={settings?.ta_requires_visit ?? true}
                                onChange={(val) => handleToggle("ta_requires_visit", val)}
                                saving={saving}
                            />
                        </SettingsSection>

                        {/* ── Location Update ───────────────────────── */}
                        <SettingsSection
                            title="Location Update"
                            description="Controls whether field employees can update doctor and firm GPS coordinates from the mobile app."
                            icon={MapPin}
                        >
                            <ToggleRow
                                label="Enable Location Update for Doctors / Firms"
                                description="When ON, employees can update the latitude and longitude of doctors and firms directly from their mobile device using current GPS or a map pin. When OFF, location update is hidden in the mobile app."
                                checked={settings?.location_update_enabled ?? false}
                                onChange={(val) => handleToggle("location_update_enabled", val)}
                                saving={saving}
                            />
                        </SettingsSection>
                    </div>
                )}
            </div>

            <Toast toast={toast} />
        </PageLayout>
    );
};

export default SettingsPage;
